/**
 * Sheet view editing (docs/specs/database-formulas.md § Phase 2): what a cell's editor and the clipboard show, fill and
 * paste as batch cell writes (`CellWrite`, POST /databases/:id/cells), formula reference spans for point mode and the
 * coloured highlights, F4 ($ cycling), the name box and the selection statistic. Pure; shared by web, iOS and Android.
 *
 * Rows and columns here are **sheet indices**: row 0…dataRows-1 are the data rows (rank order), then the summary rows
 * (`SheetGrid.rowIds`); column i is the database's i-th property (letter A = 0). A UI with extra rows (the entry row)
 * maps its own indices onto these.
 */
import type { CellWrite, Database, PropertyDef, PropertyType, PropertyValue, Row, SummaryCell, SummaryRow } from "@bundle/contracts";
import { cellInputValue, resultText, type DatabaseCache } from "./database.ts";
import { columnIndex, columnLetter } from "./formula.ts";
import { sheetFormulaDisplay, shiftFormula, fillSeries, type SheetContext } from "./sheet.ts";

/** Inclusive range of sheet cells (top ≤ bottom, left ≤ right). */
export type SheetRange = { top: number; left: number; bottom: number; right: number };

/**
 * One cell's content for fill and paste: `text` as typed (the editor's text; values as `cellInputValue` reads them),
 * `expression` (stored form) when it is a formula, `value` when it is a typed value (people, relations and files only
 * copy as values).
 */
export type SheetContent = { text: string; expression?: string; value?: PropertyValue };

/** The result text an optimistic cell formula shows until the server answers. */
export const SHEET_PENDING = "…";

const COMPUTED: PropertyType[] = ["formula", "rollup", "created_time", "created_by", "edited_time", "edited_by"];

/** Data cells of this column can be written from the Sheet (not formula, rollup or timestamps / authors). */
export function sheetColumnEditable(prop: PropertyDef): boolean {
  return !COMPUTED.includes(prop.type);
}

const tidyNumber = (n: number) => String(Number(n.toPrecision(15)));

function optionName(prop: PropertyDef, id: string): string {
  const options = prop.type === "status" ? (prop.statusOptions ?? []) : (prop.options ?? []);
  return options.find((o) => o.id === id)?.name ?? "";
}

/**
 * A data cell as its editor and the formula bar show it: `=` and the formula as typed for a cell formula (A1 in this
 * grid), plain text for text cells, numbers without formatting, dates as YYYY-MM-DD, TRUE/FALSE, option names
 * (multi-select joined with ", "). Formula and rollup columns show their result; people, relations and files "".
 */
export function sheetInputText(prop: PropertyDef, value: PropertyValue | undefined, ctx: SheetContext): string {
  if (value === undefined) return "";
  switch (value.type) {
    case "cell_formula":
      return `=${sheetFormulaDisplay(value.expression, ctx)}`;
    case "title":
    case "text":
      return value.text;
    case "url":
      return value.url;
    case "email":
      return value.email;
    case "number":
      return tidyNumber(value.number);
    case "date":
      return value.date.start;
    case "checkbox":
      return value.checked ? "TRUE" : "FALSE";
    case "select":
    case "status":
      return optionName(prop, value.optionId);
    case "multi_select":
      return value.optionIds.map((id) => optionName(prop, id)).filter((n) => n !== "").join(", ");
    case "formula":
    case "rollup":
      return resultText(value.result);
    default:
      return "";
  }
}

/** A summary cell as its editor shows it: its text, or `=` and the formula as typed. */
export function summaryInputText(cell: SummaryCell | undefined, ctx: SheetContext): string {
  if (cell === undefined) return "";
  if (cell.expression !== undefined) return `=${sheetFormulaDisplay(cell.expression, ctx)}`;
  return cell.text ?? "";
}

/** What copying a data cell puts on the clipboard as text: a formula's result, otherwise the editor's text. */
export function sheetCopyText(prop: PropertyDef, value: PropertyValue | undefined, ctx: SheetContext): string {
  if (value?.type === "cell_formula") return resultText(value.result);
  return sheetInputText(prop, value, ctx);
}

/** A data cell's fill / paste content (formula, typed value and its text). */
export function sheetContent(prop: PropertyDef, value: PropertyValue | undefined, ctx: SheetContext): SheetContent {
  if (value === undefined) return { text: "" };
  if (value.type === "cell_formula") return { text: sheetInputText(prop, value, ctx), expression: value.expression };
  if (value.type === "formula" || value.type === "rollup") return { text: resultText(value.result) };
  return { text: sheetInputText(prop, value, ctx), value };
}

/** A summary cell's fill / paste content. */
export function summaryContent(cell: SummaryCell | undefined, ctx: SheetContext): SheetContent {
  if (cell === undefined) return { text: "" };
  if (cell.expression !== undefined) return { text: summaryInputText(cell, ctx), expression: cell.expression };
  return { text: cell.text ?? "" };
}

// ---------- Clipboard text (TSV, as Sheets and Excel exchange it) ----------

/**
 * Tab-separated text → rows of cells: tabs split cells, newlines (\n, \r\n, \r) split rows, a cell starting with `"`
 * is quoted (`""` escapes; tabs and newlines inside are kept). One trailing newline is dropped; "" is no rows.
 */
export function parseTsv(text: string): string[][] {
  if (text === "") return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let i = 0;
  let atStart = true;
  while (i < text.length) {
    const ch = text[i]!;
    if (atStart && ch === '"') {
      let j = i + 1;
      let quoted = "";
      let closed = false;
      while (j < text.length) {
        if (text[j] === '"') {
          if (text[j + 1] === '"') {
            quoted += '"';
            j += 2;
            continue;
          }
          closed = true;
          j++;
          break;
        }
        quoted += text[j];
        j++;
      }
      const next = text[j];
      if (closed && (next === undefined || next === "\t" || next === "\n" || next === "\r")) {
        cell = quoted;
        i = j;
        atStart = false;
        continue;
      }
      // Not a well-formed quoted cell: read it literally.
    }
    atStart = false;
    if (ch === "\t") {
      row.push(cell);
      cell = "";
      atStart = true;
      i++;
    } else if (ch === "\n" || ch === "\r") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      atStart = true;
      i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1;
    } else {
      cell += ch;
      i++;
    }
  }
  if (!atStart || cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

/** Rows of cells → tab-separated text (cells with a tab, newline or quote are quoted, `"` doubled). */
export function toTsv(rows: string[][]): string {
  const q = (s: string) => (/[\t\n\r"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  return rows.map((r) => r.map(q).join("\t")).join("\n");
}

// ---------- Writes ----------

/** The write that puts `content` (copied from `dRows` rows above / `dCols` columns left) into a sheet cell; undefined to skip. */
function writeFor(db: Database, ctx: SheetContext, row: number, col: number, content: SheetContent, dRows: number, dCols: number): CellWrite | undefined {
  const prop = db.properties[col];
  const rowId = ctx.grid.rowIds[row];
  if (prop === undefined || rowId === undefined) return undefined;
  const summary = row >= ctx.grid.dataRows;
  if (!summary && !sheetColumnEditable(prop)) return undefined;
  const target: CellWrite = summary ? { summaryRowId: rowId, propertyId: prop.id } : { rowId, propertyId: prop.id };
  if (content.expression !== undefined) return { ...target, expression: shiftFormula(content.expression, dRows, dCols, ctx) };
  if (!summary && content.value !== undefined && content.value.type === prop.type) return { ...target, value: content.value };
  if (content.text === "") return content.value === undefined ? { ...target, value: null } : undefined;
  return { ...target, text: content.text };
}

const height = (r: SheetRange) => r.bottom - r.top + 1;
const width = (r: SheetRange) => r.right - r.left + 1;

/**
 * The writes of a fill-handle drag (or ⌘D / ⌘R) from `source` to `target` (which contains it and extends it in one
 * direction), as Sheets fills. `contents` is the source range (rows × columns). Per line along the fill: when the
 * line holds a formula or a value that only copies (people, relations, files), the source repeats, formulas shifted
 * by their offset (`shiftFormula`); otherwise the texts continue as a series (`fillSeries`; filling up or left runs
 * it backwards). Computed columns are skipped.
 */
export function sheetFillWrites(source: SheetRange, target: SheetRange, contents: SheetContent[][], db: Database, ctx: SheetContext): CellWrite[] {
  const out: CellWrite[] = [];
  const h = height(source);
  const w = width(source);
  const vertical = target.top < source.top || target.bottom > source.bottom;
  const backward = vertical ? target.top < source.top : target.left < source.left;
  const count = vertical ? height(target) - h : width(target) - w;
  if (count <= 0) return out;
  const lines = vertical ? w : h;
  const len = vertical ? h : w;
  for (let l = 0; l < lines; l++) {
    const line: SheetContent[] = Array.from({ length: len }, (_, k) => (vertical ? contents[k]?.[l] : contents[l]?.[k]) ?? { text: "" });
    const repeats = line.some((c) => c.expression !== undefined || (c.value !== undefined && c.text === ""));
    const ordered = backward ? line.toReversed() : line;
    const series = repeats ? undefined : fillSeries(ordered.map((c) => c.text), count);
    for (let i = 0; i < count; i++) {
      const k = i % len;
      const srcIdx = backward ? len - 1 - k : k;
      const along = backward ? (vertical ? source.top : source.left) - 1 - i : (vertical ? source.bottom : source.right) + 1 + i;
      const srcAlong = (vertical ? source.top : source.left) + srcIdx;
      const row = vertical ? along : source.top + l;
      const col = vertical ? source.left + l : along;
      const content: SheetContent = series === undefined ? line[srcIdx]! : { text: series[i]! };
      const dRows = vertical ? along - srcAlong : 0;
      const dCols = vertical ? 0 : along - srcAlong;
      const write = writeFor(db, ctx, row, col, content, dRows, dCols);
      if (write !== undefined) out.push(write);
    }
  }
  return out;
}

/**
 * The writes of a paste at `selection` (its top-left cell first). `clip` is the pasted block (rows × columns).
 * `from` is where the block was copied in this sheet (an internal copy: formulas shift by the offset, values stay
 * typed); without it the texts are typed input (`=…` is a formula in A1). A block that fits a whole number of times
 * into a bigger selection (a single cell included) repeats over it, as in Sheets. Cells past the grid are dropped.
 */
export function sheetPasteWrites(selection: SheetRange, clip: SheetContent[][], from: { row: number; col: number } | undefined, db: Database, ctx: SheetContext): CellWrite[] {
  const out: CellWrite[] = [];
  const ch = clip.length;
  const cw = Math.max(0, ...clip.map((r) => r.length));
  if (ch === 0 || cw === 0) return out;
  const sh = height(selection);
  const sw = width(selection);
  const tileRows = sh % ch === 0 ? sh / ch : 1;
  const tileCols = sw % cw === 0 ? sw / cw : 1;
  const rows = ctx.grid.rowIds.length;
  const cols = db.properties.length;
  for (let tr = 0; tr < tileRows; tr++) {
    for (let tc = 0; tc < tileCols; tc++) {
      for (let r = 0; r < ch; r++) {
        for (let c = 0; c < cw; c++) {
          const row = selection.top + tr * ch + r;
          const col = selection.left + tc * cw + c;
          if (row >= rows || col >= cols) continue;
          const content = clip[r]?.[c] ?? { text: "" };
          const pasted: SheetContent = from === undefined ? { text: content.text } : content;
          const write = writeFor(db, ctx, row, col, pasted, from === undefined ? 0 : row - (from.row + r), from === undefined ? 0 : col - (from.col + c));
          if (write !== undefined) out.push(write);
        }
      }
    }
  }
  return out;
}

/** Clearing a range: every editable data cell and every summary cell to empty. */
export function sheetClearWrites(range: SheetRange, db: Database, ctx: SheetContext): CellWrite[] {
  const out: CellWrite[] = [];
  for (let row = range.top; row <= range.bottom; row++) {
    for (let col = range.left; col <= range.right; col++) {
      const w = writeFor(db, ctx, row, col, { text: "" }, 0, 0);
      if (w !== undefined) out.push(w);
    }
  }
  return out;
}

/**
 * The first write a column would refuse (typed columns stay typed): text on a data row that isn't a formula and that
 * `cellInputValue` can't read for the column. The server refuses the whole batch in that case.
 */
export function sheetWriteProblem(writes: CellWrite[], db: Database): { propertyId: string; text: string } | undefined {
  for (const w of writes) {
    if (w.rowId === undefined || w.text === undefined || w.text.trimStart().startsWith("=")) continue;
    const prop = db.properties.find((p) => p.id === w.propertyId);
    if (prop !== undefined && cellInputValue(prop, w.text) === undefined) return { propertyId: prop.id, text: w.text };
  }
  return undefined;
}

// ---------- Typed references: spans, F4, point mode, name box ----------

export type SheetRefSpan = {
  start: number;
  end: number;
  text: string;
  /** formulaRef.1…6, one per distinct reference in order (cycling), as in Sheets. */
  color: 1 | 2 | 3 | 4 | 5 | 6;
  /** The cells it covers in this grid (absent for other databases and cells past the grid). */
  range?: SheetRange;
};

const REF_P = String.raw`(?<![\p{L}\p{N}_.$!'"])(?:(?:'(?:[^']|'')+'|[\p{L}_][\p{L}\p{N}_.]*)!)?(?:\$?[A-Za-z]{1,3}\$?[0-9]{1,7}(?::\$?[A-Za-z]{1,3}\$?[0-9]{1,7})?|\$?[A-Za-z]{1,3}:\$?[A-Za-z]{1,3})(?![\p{L}\p{N}_(!])`;

/** Text with string literals blanked out (same length), so references inside quotes are skipped. */
function blankStrings(text: string): string {
  return text.replace(/"(?:[^"]|"")*"?/g, (m) => " ".repeat(m.length));
}

function a1Cell(s: string): { row: number; col: number } | undefined {
  const m = /^\$?([A-Za-z]{1,3})\$?([0-9]{1,7})$/.exec(s);
  if (m === null) return undefined;
  return { col: columnIndex(m[1]!), row: Number(m[2]) - 1 };
}

/** The sheet cells an A1 reference (`C2`, `B1:C3`, `C:C`; no sheet prefix) covers; undefined past the grid. */
function a1Range(ref: string, ctx: SheetContext): SheetRange | undefined {
  const rows = ctx.grid.rowIds.length;
  const cols = ctx.grid.properties.length;
  const [a, b] = ref.split(":");
  const colOnly = /^\$?[A-Za-z]{1,3}$/;
  if (b !== undefined && colOnly.test(a!) && colOnly.test(b)) {
    const l = columnIndex(a!.replace("$", ""));
    const r = columnIndex(b.replace("$", ""));
    if (l < 0 || r < 0 || Math.max(l, r) >= cols || ctx.grid.dataRows === 0) return undefined;
    return { top: 0, left: Math.min(l, r), bottom: ctx.grid.dataRows - 1, right: Math.max(l, r) };
  }
  const x = a1Cell(a!);
  const y = b === undefined ? x : a1Cell(b);
  if (x === undefined || y === undefined) return undefined;
  const range = { top: Math.min(x.row, y.row), left: Math.min(x.col, y.col), bottom: Math.max(x.row, y.row), right: Math.max(x.col, y.col) };
  return range.top < 0 || range.bottom >= rows || range.right >= cols ? undefined : range;
}

/**
 * The references in typed formula text (string literals skipped), with their colour and, for this grid, the cells
 * they cover — the formula bar's coloured chips and the grid's dashed highlights while editing.
 */
export function sheetRefSpans(text: string, ctx: SheetContext): SheetRefSpan[] {
  if (!text.startsWith("=")) return [];
  const out: SheetRefSpan[] = [];
  const colors = new Map<string, number>();
  const re = new RegExp(REF_P, "gu");
  for (const m of blankStrings(text).matchAll(re)) {
    const raw = text.slice(m.index, m.index + m[0].length);
    const bang = raw.lastIndexOf("!");
    const sheet = bang < 0 ? undefined : raw.slice(0, bang).replace(/^'|'$/g, "").replace(/''/g, "'");
    const ref = bang < 0 ? raw : raw.slice(bang + 1);
    const key = raw.replace(/\$/g, "").toUpperCase();
    if (!colors.has(key)) colors.set(key, colors.size % 6);
    const span: SheetRefSpan = { start: m.index, end: m.index + raw.length, text: raw, color: (colors.get(key)! + 1) as SheetRefSpan["color"] };
    const own = sheet === undefined || sheet.toLowerCase() === ctx.grid.title.toLowerCase();
    const range = own ? a1Range(ref, ctx) : undefined;
    if (range !== undefined) span.range = range;
    out.push(span);
  }
  return out;
}

/**
 * F4 on typed formula text: the A1 reference at the caret (or ending at it) cycles C7 → $C$7 → C$7 → $C7 → C7, a range
 * as a whole. Returns the new text and caret (after the reference); unchanged when the caret isn't on one.
 */
export function cycleAbsolute(text: string, caret: number): { text: string; caret: number } {
  const re = /(?<![\p{L}\p{N}_.])(\$?[A-Za-z]{1,3}\$?[0-9]{1,7})(?::(\$?[A-Za-z]{1,3}\$?[0-9]{1,7}))?(?![\p{L}\p{N}_(])/gu;
  for (const m of blankStrings(text).matchAll(re)) {
    const start = m.index;
    const end = start + m[0].length;
    if (caret < start || caret > end) continue;
    const parts = text.slice(start, end).split(":");
    const first = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]+)$/.exec(parts[0]!)!;
    const absCol = first[1] === "$";
    const absRow = first[3] === "$";
    // A1 → $A$1 → A$1 → $A1 → A1
    const [nextCol, nextRow] = !absCol && !absRow ? [true, true] : absCol && absRow ? [false, true] : !absCol && absRow ? [true, false] : [false, false];
    const shown = parts.map((p) => {
      const x = /^\$?([A-Za-z]{1,3})\$?([0-9]+)$/.exec(p)!;
      return `${nextCol ? "$" : ""}${x[1]}${nextRow ? "$" : ""}${x[2]}`;
    });
    const ref = shown.join(":");
    return { text: text.slice(0, start) + ref + text.slice(end), caret: start + ref.length };
  }
  return { text, caret };
}

/**
 * Whether clicking a cell while editing inserts its reference at the caret (point mode): the text is a formula and
 * the caret follows `=`, `(`, `,`, `;` or an operator (spaces skipped), with no name or number right after it.
 */
export function pointModeAt(text: string, caret: number): boolean {
  if (!text.startsWith("=") || caret < 1 || caret > text.length) return false;
  let i = caret - 1;
  while (i > 0 && text[i] === " ") i--;
  const before = text[i]!;
  const after = text[caret] ?? "";
  if (/[\p{L}\p{N}_$'"]/u.test(after)) return false;
  return "=(,;+-*/^&<>".includes(before);
}

/** A1 text of a range of sheet cells: "B4", or "A1:C3" (1-based rows). */
export function sheetRangeName(range: SheetRange): string {
  const a = `${columnLetter(range.left)}${range.top + 1}`;
  const b = `${columnLetter(range.right)}${range.bottom + 1}`;
  return a === b ? a : `${a}:${b}`;
}

/** The name box's typed text ("B4", "a1:c3", "$B$4") as a range of this grid; undefined when it isn't one. */
export function parseSheetRangeName(text: string, ctx: SheetContext): SheetRange | undefined {
  const t = text.trim();
  if (!/^\$?[A-Za-z]{1,3}\$?[0-9]{1,7}(:\$?[A-Za-z]{1,3}\$?[0-9]{1,7})?$/.test(t)) return undefined;
  return a1Range(t, ctx);
}

/** The selection statistic over the numbers in a selection and how many of its cells are filled (Sheets' status bar). */
export function sheetSelectionStats(numbers: number[], filled: number): { sum: number; average?: number; min?: number; max?: number; count: number; numbers: number } {
  const sum = Number(numbers.reduce((a, b) => a + b, 0).toPrecision(15));
  const out: { sum: number; average?: number; min?: number; max?: number; count: number; numbers: number } = { sum, count: filled, numbers: numbers.length };
  if (numbers.length > 0) {
    out.average = Number((sum / numbers.length).toPrecision(15));
    out.min = Math.min(...numbers);
    out.max = Math.max(...numbers);
  }
  return out;
}

/**
 * The format the selection statistic shows Sum, Average, Min and Max in (Sheets): the one number format every number
 * in the selection is shown with (each column's), or plain numbers when they're mixed or there are none.
 */
export function sheetStatsFormat(formats: ("number" | "percent" | "currency")[]): "number" | "percent" | "currency" {
  const first = formats[0];
  return first !== undefined && formats.every((f) => f === first) ? first : "number";
}

// ---------- Optimistic state (data sources apply these until the server's answer replaces them) ----------

const pendingResult = { kind: "text" as const, text: SHEET_PENDING };

/** Whether a stored cell-formula / summary result is the optimistic placeholder (shown as "…"). */
export function isPendingResult(result: { kind: string; text?: string } | undefined): boolean {
  return result !== undefined && result.kind === "text" && result.text === SHEET_PENDING;
}

/**
 * A batch of cell writes applied locally: values at once (text read with `cellInputValue`; text that doesn't fit is
 * left to the server's refusal), formulas (`=…` text or an expression) as cell formulas with a pending result, summary
 * cells as text or pending formulas; null / "" clears.
 */
export function applyCellWrites(cache: DatabaseCache, writes: CellWrite[], at: number, meId: string): DatabaseCache {
  const props = new Map(cache.database.properties.map((p) => [p.id, p]));
  const rows = new Map<string, Row>();
  let summary: SummaryRow[] | undefined;
  for (const w of writes) {
    const prop = props.get(w.propertyId);
    if (prop === undefined) continue;
    const typed = w.text?.trimStart();
    const formula = w.expression ?? (typed !== undefined && typed.startsWith("=") ? typed.slice(1) : undefined);
    if (w.rowId !== undefined) {
      const base = rows.get(w.rowId) ?? cache.rows.find((r) => r.id === w.rowId);
      if (base === undefined || !sheetColumnEditable(prop)) continue;
      const values = { ...base.values };
      let value: PropertyValue | null | undefined = w.value;
      if (formula !== undefined) value = { type: "cell_formula", expression: formula, result: pendingResult };
      else if (w.text !== undefined) value = cellInputValue(prop, w.text);
      if (value === undefined) continue;
      if (value === null) delete values[prop.id];
      else values[prop.id] = value;
      rows.set(base.id, { ...base, values, updatedAt: at, updatedBy: meId });
    } else if (w.summaryRowId !== undefined) {
      summary ??= (cache.database.summaryRows ?? []).map((s) => ({ ...s, cells: { ...s.cells } }));
      const s = summary.find((x) => x.id === w.summaryRowId);
      if (s === undefined) continue;
      if (formula !== undefined) s.cells[prop.id] = { expression: formula, result: pendingResult };
      else if (w.text !== undefined && w.text !== "") s.cells[prop.id] = { text: w.text };
      else delete s.cells[prop.id];
    }
  }
  if (rows.size === 0 && summary === undefined) return cache;
  const out: DatabaseCache = { ...cache, rows: cache.rows.map((r) => rows.get(r.id) ?? r) };
  if (summary !== undefined) out.database = { ...cache.database, summaryRows: summary };
  return out;
}

/** The database with a new empty summary row `id` at `index` (default last). */
export function insertSummaryRow(db: Database, id: string, index?: number): Database {
  const list = db.summaryRows ?? [];
  if (list.some((s) => s.id === id)) return db;
  const next = [...list];
  next.splice(Math.max(0, Math.min(index ?? list.length, list.length)), 0, { id, cells: {} });
  return { ...db, summaryRows: next };
}

/** The database with summary row `id` moved to `index`. */
export function moveSummaryRow(db: Database, id: string, index: number): Database {
  const list = db.summaryRows ?? [];
  const i = list.findIndex((s) => s.id === id);
  if (i < 0) return db;
  const next = [...list];
  const [s] = next.splice(i, 1);
  next.splice(Math.max(0, Math.min(index, next.length)), 0, s!);
  return { ...db, summaryRows: next };
}

/** The database without summary row `id`. */
export function removeSummaryRow(db: Database, id: string): Database {
  const list = db.summaryRows ?? [];
  if (!list.some((s) => s.id === id)) return db;
  const next = list.filter((s) => s.id !== id);
  const out: Database = { ...db, summaryRows: next };
  if (next.length === 0) delete out.summaryRows;
  return out;
}
